import { FieldEdit } from '@/App/tiles/Option/strategy/FieldEdit.tsx';
import { getFieldRow, getFieldSpan, isHedgeField } from '@/App/tiles/Option/strategy/strategyLayout.ts';
import type { StrategyFields, StrategyOrientation } from '@/App/tiles/Option/strategy/types.ts';
import type { LegsData, OptionLegProperties } from '@/store/option/state.ts';

type SharedHedgeFieldsProps = {
  tileId: string;
  legs: OptionLegProperties[];
  quoteLeg: LegsData | undefined;
  fields: StrategyFields;
  groupColumn: number;
  isOpen: boolean;
  mode: StrategyOrientation;
};

/** Shared editors use the first leg; the parent owns grouping and summary placement. */
export function SharedHedgeFields({
  tileId,
  legs,
  quoteLeg,
  fields,
  groupColumn,
  isOpen,
  mode,
}: SharedHedgeFieldsProps) {
  const firstLeg = legs[0];

  if (firstLeg === undefined) {
    return undefined;
  }

  const editorProps = { tileId, leg: firstLeg, legId: firstLeg.legId, quoteLeg, visible: true };

  if (mode === 'horizontal') {
    return fields
      .flat()
      .map((field, index) =>
        isHedgeField(field) ? (
          <FieldEdit key={field} {...editorProps} field={field} style={{ gridColumn: index + 2, gridRow: 1 }} />
        ) : undefined,
      );
  }

  return fields.map((fieldOrGroup, index) => {
    const row = getFieldRow(fields, index);

    if (!Array.isArray(fieldOrGroup)) {
      return (
        isHedgeField(fieldOrGroup) && (
          <FieldEdit
            key={fieldOrGroup}
            {...editorProps}
            field={fieldOrGroup}
            style={{ gridColumn: `${groupColumn} / span ${isOpen ? legs.length : 1}`, gridRow: row }}
          />
        )
      );
    }

    if (!fieldOrGroup.some(isHedgeField)) {
      return undefined;
    }
    return (
      <div
        key={fieldOrGroup.join('-')}
        className="fieldGroup shared-hedge-fields bg-lvl2"
        style={{
          gridColumn: `${groupColumn} / span ${isOpen ? legs.length : 1}`,
          gridRow: `${row} / span ${getFieldSpan(fieldOrGroup)}`,
        }}
      >
        {fieldOrGroup
          .filter((field) => isHedgeField(field))
          .map((field) => (
            <FieldEdit key={field} {...editorProps} field={field} />
          ))}
      </div>
    );
  });
}
